import React, { useContext, useState } from 'react';
import { GlobalInfo } from "./context";
import { FinanceOnly } from "./Common";
import { database, globalSettings } from "./config";
import { numberWithCommas } from "./util";

// Sheet details
const SHEET_ID = '14bxmYxcF0-RFPwVInc6e6Yxqt3CNi33IHb4qMsX4ogI';
const SHEET_GID = '1182008591';

export default function SyncPricesPage() {
  const context = useContext(GlobalInfo);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncComplete, setSyncComplete] = useState(false);
  const [selectedItems, setSelectedItems] = useState({});
  const [selectedMultipliers, setSelectedMultipliers] = useState({});
  const [lastSync, setLastSync] = useState(null);

  // Fetch last sync info on mount
  React.useEffect(() => {
    const fetchLastSync = async () => {
      try {
        const snapshot = await database.collection('syncAuditLog')
          .orderBy('timestamp', 'desc')
          .limit(1)
          .get();

        if (!snapshot.empty) {
          const doc = snapshot.docs[0];
          setLastSync(doc.data());
        }
      } catch (err) {
        console.error('Failed to fetch last sync:', err);
      }
    };
    fetchLastSync();
  }, [syncComplete]); // Refetch after a sync completes

  // Parse CSV data from Google Sheets
  const parseCSV = (csvText) => {
    const lines = csvText.split('\n');
    const data = [];

    // Proper CSV parsing that handles quoted fields with commas
    const parseCSVLine = (line) => {
      const result = [];
      let current = '';
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];

        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current.trim()); // Don't forget the last field
      return result;
    };

    let sheetOrder = 0;
    for (let i = 1; i < lines.length; i++) { // Skip header row
      const line = lines[i];
      if (!line.trim()) continue;

      const fields = parseCSVLine(line);

      if (fields.length >= 5) {
        const name = fields[0].replace(/"/g, '').trim(); // Column A - Material name
        const marketBasePrice = parseFloat(fields[2].replace(/"/g, '').replace(/,/g, '')) || 0; // Column C
        // Column D contains the buyback % (e.g., "80.00%" or "80")
        const buybackPctRaw = fields[3].replace(/"/g, '').replace(/%/g, '').trim();
        const sheetBuybackPct = parseFloat(buybackPctRaw) || 80; // Column D - Buyback %
        const buybackPrice = parseFloat(fields[4].replace(/"/g, '').replace(/,/g, '')) || 0; // Column E

        if (name && marketBasePrice > 0) {
          data.push({
            name,
            marketBasePrice,
            sheetBuybackPct,
            buybackPrice,
            sheetOrder: sheetOrder++, // Track original sheet order
          });
        }
      }
    }
    return data;
  };

  // Fetch data from Google Sheets (exported as CSV)
  const fetchSheetData = async () => {
    setLoading(true);
    setError(null);
    setSyncComplete(false);

    try {
      // Fetch sheet as CSV (requires sheet to be published or shared with "Anyone with link")
      const csvUrl = `https://docs.google.com/spreadsheets/d/${SHEET_ID}/export?format=csv&gid=${SHEET_GID}`;

      const response = await fetch(csvUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch sheet: ${response.status}. Make sure the sheet is shared with "Anyone with link can view".`);
      }

      const csvText = await response.text();
      const data = parseCSV(csvText);

      if (data.length === 0) {
        throw new Error('No valid data found in the sheet. Check that columns A, C, and E contain the expected data.');
      }

      // Build comparison with current database values - ONLY for items that exist in DB
      const comparisonData = data
        .filter(item => {
          // Only include items that exist in our materials database
          return !!context.materialsMap[item.name];
        })
        .map(item => {
          const material = context.materialsMap[item.name];
          const currentPrice = material.price || 0;
          const currentMultiplier = material.multiplier || 100;
          const newPrice = item.buybackPrice;

          // Sheet's buyback % comes directly from Column D
          const sheetBuybackPct = item.sheetBuybackPct;

          // App's current effective buyback % = multiplier * base buyback rate
          const appBuybackPct = (currentMultiplier / 100) * globalSettings.buybackMultiplier * 100;

          // Calculate what multiplier is needed to match sheet's buyback %
          // sheetBuybackPct = (neededMultiplier/100) * buybackMultiplier * 100
          // neededMultiplier = sheetBuybackPct / buybackMultiplier
          const neededMultiplier = sheetBuybackPct / globalSettings.buybackMultiplier;

          // Calculate expected buyback from our calculation (using current multiplier)
          const calculatedBuyback = newPrice * (currentMultiplier / 100) * globalSettings.buybackMultiplier;

          // Check if buyback rates match (within 5%)
          const buybackPctDiff = Math.abs(appBuybackPct - sheetBuybackPct);
          const buybackValid = buybackPctDiff <= 5;

          // Check if multiplier needs updating
          const multiplierNeedsUpdate = Math.abs(currentMultiplier - neededMultiplier) > 1;

          // Calculate price change percentage
          const priceChange = currentPrice > 0
            ? ((newPrice - currentPrice) / currentPrice * 100)
            : (newPrice > 0 ? 100 : 0);

          return {
            name: item.name,
            currentPrice,
            newPrice,
            priceChange,
            sheetBuyback: item.buybackPrice,
            calculatedBuyback,
            sheetBuybackPct,
            appBuybackPct,
            currentMultiplier,
            neededMultiplier,
            multiplierNeedsUpdate,
            buybackPctDiff,
            buybackValid,
            sheetOrder: item.sheetOrder, // Preserve sheet order
          };
        });

      // Sort by original sheet order (Column A order)
      comparisonData.sort((a, b) => a.sheetOrder - b.sheetOrder);

      setComparison(comparisonData);

      // Select all items for price sync by default
      const initialSelection = {};
      const initialMultiplierSelection = {};
      comparisonData.forEach(item => {
        initialSelection[item.name] = true;
        // Auto-select multiplier update for items that need it
        if (item.multiplierNeedsUpdate) {
          initialMultiplierSelection[item.name] = true;
        }
      });
      setSelectedItems(initialSelection);
      setSelectedMultipliers(initialMultiplierSelection);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Toggle item selection
  const toggleItem = (name) => {
    setSelectedItems(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  // Toggle multiplier selection
  const toggleMultiplier = (name) => {
    setSelectedMultipliers(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  // Select/deselect all prices
  const toggleAllPrices = (select) => {
    const newSelection = {};
    comparison.forEach(item => {
      newSelection[item.name] = select;
    });
    setSelectedItems(newSelection);
  };

  // Select/deselect all multipliers
  const toggleAllMultipliers = (select) => {
    const newSelection = {};
    comparison.forEach(item => {
      if (item.multiplierNeedsUpdate) {
        newSelection[item.name] = select;
      }
    });
    setSelectedMultipliers(newSelection);
  };

  // Sync selected prices and multipliers to database
  const syncPrices = async () => {
    const itemsToSyncPrice = comparison.filter(item => selectedItems[item.name]);
    const itemsToSyncMultiplier = comparison.filter(item => selectedMultipliers[item.name]);

    if (itemsToSyncPrice.length === 0 && itemsToSyncMultiplier.length === 0) {
      setError('No items selected for sync');
      return;
    }

    setSyncing(true);
    setError(null);

    try {
      const batch = database.batch();

      // Build audit log of changes
      const priceChanges = [];
      const multiplierChanges = [];

      // Update prices
      itemsToSyncPrice.forEach(item => {
        const docRef = database.collection('materials').doc(item.name);
        batch.update(docRef, {
          price: item.newPrice,
          priceUpdated: Date.now(),
          priceUpdatedBy: {
            id: context.userInfo.id,
            name: context.userInfo.name,
          },
          priceSource: 'PEGN Build Sheet',
        });

        // Record for audit log
        priceChanges.push({
          material: item.name,
          oldPrice: item.currentPrice,
          newPrice: item.newPrice,
        });
      });

      // Update multipliers
      itemsToSyncMultiplier.forEach(item => {
        const docRef = database.collection('materials').doc(item.name);
        const newMultiplier = Math.round(item.neededMultiplier * 10) / 10;
        batch.update(docRef, {
          multiplier: newMultiplier,
        });

        // Record for audit log
        multiplierChanges.push({
          material: item.name,
          oldMultiplier: item.currentMultiplier,
          newMultiplier: newMultiplier,
          oldBuybackPct: item.appBuybackPct,
          newBuybackPct: item.sheetBuybackPct,
        });
      });

      // Create audit log entry
      const auditLogRef = database.collection('syncAuditLog').doc();
      batch.set(auditLogRef, {
        timestamp: Date.now(),
        syncedBy: {
          id: context.userInfo.id,
          name: context.userInfo.name,
        },
        source: 'PEGN Build Sheet',
        summary: {
          priceUpdates: priceChanges.length,
          multiplierUpdates: multiplierChanges.length,
        },
        priceChanges: priceChanges,
        multiplierChanges: multiplierChanges,
      });

      await batch.commit();
      setSyncComplete(true);

    } catch (err) {
      setError(`Failed to sync: ${err.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const selectedPriceCount = Object.values(selectedItems).filter(Boolean).length;
  const selectedMultiplierCount = Object.values(selectedMultipliers).filter(Boolean).length;
  const totalCount = comparison?.length || 0;
  const needsMultiplierCount = comparison?.filter(item => item.multiplierNeedsUpdate).length || 0;

  return (
    <FinanceOnly>
      <div className="container-fluid sync-prices-page">
        <h2 className="mt-4 mb-4">Sync Prices with PEGN Build Sheet</h2>

        {lastSync && (
          <div className="alert alert-secondary mb-3">
            <strong>Last synced by</strong> {lastSync.syncedBy?.name || 'Unknown'} <strong>at</strong> {new Date(lastSync.timestamp).toLocaleString()}
            <span className="ml-3 text-muted">
              ({lastSync.summary?.priceUpdates || 0} prices, {lastSync.summary?.multiplierUpdates || 0} multipliers)
            </span>
          </div>
        )}

        <div className="alert alert-info">
          <strong>How this works:</strong>
          <ul className="mb-0">
            <li>Fetches "Buyback Price" (Column E) from the PEGN Build Sheet</li>
            <li><strong>Price checkbox:</strong> Updates the price in the app to match the sheet's buyback price</li>
            <li><strong>Match Sheet Rate checkbox:</strong> Adjusts the per-item multiplier so the app's buyback % matches the sheet's rate</li>
            <li>Items with mismatched rates are highlighted. Check the box to update the app rate to match the sheet.</li>
          </ul>
        </div>

        {!comparison && (
          <button
            className="btn btn-primary btn-lg"
            onClick={fetchSheetData}
            disabled={loading}
          >
            {loading ? 'Fetching...' : 'Fetch Prices from Sheet'}
          </button>
        )}

        {error && (
          <div className="alert alert-danger mt-3">
            <strong>Error:</strong> {error}
          </div>
        )}

        {syncComplete && (
          <div className="alert alert-success mt-3">
            <strong>Success!</strong> Prices and multipliers have been synced to the database.
            The changes will appear in the app shortly.
          </div>
        )}

        {comparison && (
          <>
            <div className="mb-3 d-flex justify-content-between align-items-center flex-wrap">
              <div className="mb-2">
                <span className="badge badge-info mr-2">{totalCount} items</span>
                <span className="badge badge-warning mr-2">{needsMultiplierCount} need multiplier update</span>
                <span className="badge badge-primary mr-2">{selectedPriceCount} prices selected</span>
                <span className="badge badge-success">{selectedMultiplierCount} multipliers selected</span>
              </div>
              <div>
                <button className="btn btn-outline-secondary btn-sm mr-2" onClick={() => { toggleAllPrices(true); toggleAllMultipliers(true); }}>
                  Select All
                </button>
                <button className="btn btn-outline-secondary btn-sm mr-2" onClick={() => { toggleAllPrices(false); toggleAllMultipliers(false); }}>
                  Deselect All
                </button>
                <button className="btn btn-secondary mr-2" onClick={fetchSheetData} disabled={loading}>
                  Refresh
                </button>
                <button
                  className="btn btn-success"
                  onClick={syncPrices}
                  disabled={syncing || (selectedPriceCount === 0 && selectedMultiplierCount === 0)}
                >
                  {syncing ? 'Syncing...' : `Sync (${selectedPriceCount} prices, ${selectedMultiplierCount} multipliers)`}
                </button>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-striped table-bordered table-sm">
                <thead className="thead-dark">
                  <tr>
                    <th style={{width: '50px'}}>
                      <small>Price</small><br/>
                      <input
                        type="checkbox"
                        checked={selectedPriceCount === comparison.length && comparison.length > 0}
                        onChange={(e) => toggleAllPrices(e.target.checked)}
                      />
                    </th>
                    <th>Material</th>
                    <th className="text-right">Current Price</th>
                    <th className="text-right">New Price</th>
                    <th className="text-right">Change</th>
                    <th className="text-right">Sheet Rate</th>
                    <th className="text-right">App Rate</th>
                    <th className="text-right">Multiplier</th>
                    <th style={{width: '80px'}} className="text-center">
                      <small>Match Sheet Rate</small><br/>
                      <input
                        type="checkbox"
                        checked={selectedMultiplierCount === needsMultiplierCount && needsMultiplierCount > 0}
                        onChange={(e) => toggleAllMultipliers(e.target.checked)}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.map((item) => (
                    <tr
                      key={item.name}
                      className={item.multiplierNeedsUpdate ? 'table-warning' : ''}
                    >
                      <td className="text-center">
                        <input
                          type="checkbox"
                          checked={!!selectedItems[item.name]}
                          onChange={() => toggleItem(item.name)}
                        />
                      </td>
                      <td>{item.name}</td>
                      <td className="text-right">
                        {item.currentPrice > 0 ? numberWithCommas(item.currentPrice) : '-'}
                      </td>
                      <td className="text-right">{numberWithCommas(item.newPrice)}</td>
                      <td className={`text-right ${item.priceChange > 10 ? 'text-success' : item.priceChange < -10 ? 'text-danger' : ''}`}>
                        {item.currentPrice > 0 ? `${item.priceChange > 0 ? '+' : ''}${item.priceChange.toFixed(1)}%` : 'NEW'}
                      </td>
                      <td className={`text-right ${item.sheetBuybackPct < 75 ? 'text-info font-weight-bold' : ''}`}>
                        {item.sheetBuybackPct.toFixed(0)}%
                      </td>
                      <td className={`text-right ${item.multiplierNeedsUpdate ? 'text-danger' : 'text-success'}`}>
                        {item.appBuybackPct.toFixed(0)}%
                        {selectedMultipliers[item.name] && item.multiplierNeedsUpdate && (
                          <span className="text-success"> → {item.sheetBuybackPct.toFixed(0)}%</span>
                        )}
                      </td>
                      <td className="text-right">
                        {item.currentMultiplier.toFixed(0)}
                        {selectedMultipliers[item.name] && item.multiplierNeedsUpdate && (
                          <span className="text-success"> → {item.neededMultiplier.toFixed(0)}</span>
                        )}
                      </td>
                      <td className="text-center">
                        {item.multiplierNeedsUpdate ? (
                          <input
                            type="checkbox"
                            checked={!!selectedMultipliers[item.name]}
                            onChange={() => toggleMultiplier(item.name)}
                            title={`Set multiplier to ${item.neededMultiplier.toFixed(1)} to match ${item.sheetBuybackPct.toFixed(0)}% buyback rate`}
                          />
                        ) : (
                          <span className="badge badge-success">OK</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </FinanceOnly>
  );
}
